/**
 * useSuggestCategory Hook
 * Combines client-side fuzzy matching with AI backend fallback
 * for suggesting task categories from the task title.
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { suggestCategory, type SuggestCategoryRequest, type SuggestCategoryResponse } from '@signalvote/shared';
import type { CategoryInput } from '@signalvote/shared';
import { apiResponse } from './apiFetch';

interface SuggestedCategory {
  id: string;
  name: string;
}

interface UseSuggestCategoryResult {
  suggestedCategory: SuggestedCategory | null;
  isLoading: boolean;
}

/**
 * Suggest a category for a task title using fuzzy matching + AI fallback.
 * 
 * @param taskTitle - The current task title text
 * @param categories - Available categories to match against
 * @param enabled - Whether to run suggestions (false when user has manually selected a category)
 */
export function useSuggestCategory(
  taskTitle: string,
  categories: CategoryInput[],
  enabled: boolean = true
): UseSuggestCategoryResult {
  const [debouncedTitle, setDebouncedTitle] = useState('');

  // Debounce the title for AI fallback (500ms)
  useEffect(() => {
    if (!enabled || !taskTitle.trim()) {
      setDebouncedTitle('');
      return;
    }
    const timer = setTimeout(() => setDebouncedTitle(taskTitle.trim()), 500);
    return () => clearTimeout(timer);
  }, [taskTitle, enabled]);

  // Step 1: Try client-side fuzzy match (instant, no API call)
  const fuzzyResult = useMemo(() => {
    if (!enabled || !taskTitle.trim() || categories.length === 0) return null;
    return suggestCategory(taskTitle, categories);
  }, [taskTitle, categories, enabled]);

  // Step 2: AI fallback — only when fuzzy fails and title has 3+ words
  const categoryNames = useMemo(
    () => categories.map((c) => c.name),
    [categories]
  );

  const needsAiFallback =
    enabled &&
    !fuzzyResult &&
    debouncedTitle.split(/\s+/).length >= 3 &&
    categories.length > 0;

  const { data: aiResult, isLoading: aiLoading } = useQuery({
    queryKey: ['suggest-category', debouncedTitle, categoryNames],
    queryFn: async () => {
      const response = await apiResponse('/api/tasks/suggest-category', {
        method: 'POST',
        json: {
          title: debouncedTitle,
          categories: categoryNames,
        } satisfies SuggestCategoryRequest,
      });

      if (response.status === 204) return null;
      if (!response.ok) return null;

      return (await response.json()) as SuggestCategoryResponse;
    },
    enabled: needsAiFallback,
    staleTime: Infinity,
    retry: false,
  });

  // Resolve: fuzzy first, then AI fallback
  const suggestedCategory = useMemo((): SuggestedCategory | null => {
    if (!enabled) return null;

    // Fuzzy match found
    if (fuzzyResult) {
      return { id: fuzzyResult.id, name: fuzzyResult.name };
    }

    // AI fallback found
    if (aiResult?.categoryName) {
      const matched = categories.find(
        (c) => c.name.toLowerCase() === aiResult.categoryName!.toLowerCase()
      );
      if (matched) {
        return { id: matched.id, name: matched.name };
      }
    }

    return null;
  }, [enabled, fuzzyResult, aiResult, categories]);

  return {
    suggestedCategory,
    isLoading: needsAiFallback && aiLoading,
  };
}
